/**
 * Admin Dashboard
 * 
 * Platform statistics and quick overview for administrators.
 */
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import './Admin.css';
import { getAuthToken } from '../../utils/auth';
import { formatErrorMessage } from '../../utils/errorUtils';
import { useAuth } from '../../context/AuthContext';

// SVG Icons
const UsersIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
        <circle cx="9" cy="7" r="4"/>
        <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
        <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
    </svg>
);

const ActiveIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2">
        <circle cx="12" cy="12" r="10"/>
        <polyline points="12 6 12 12 16 14"/>
    </svg>
);

const FolderIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/>
    </svg>
);

const RocketIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09z"/>
        <path d="m12 15-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2z"/>
        <path d="M9 12H4s.55-3.03 2-4c1.62-1.08 5 0 5 0"/>
        <path d="M12 15v5s3.03-.55 4-2c1.08-1.62 0-5 0-5"/>
    </svg>
);

const CoinsIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="8" cy="8" r="6"/>
        <path d="M18.09 10.37A6 6 0 1 1 10.34 18"/>
        <path d="M7 6h1v4"/>
        <path d="m16.71 13.88.7.71-2.82 2.82"/>
    </svg>
);

// Mock API - replace with actual cloudApi imports
const fetchPlatformUsage = async () => {
    const CLOUD_API_BASE = process.env.REACT_APP_CLOUD_API_URL || 'http://localhost:8080';
    const token = getAuthToken();
    const response = await fetch(`${CLOUD_API_BASE}/api/admin/usage`, {
        headers: { 'Authorization': `Bearer ${token}` }
    });
    if (!response.ok) throw new Error('Failed to fetch');
    return response.json();
};

export function AdminDashboard() {
    const { isSuperAdmin } = useAuth();
    const [stats, setStats] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const loadStats = async () => {
            try {
                const data = await fetchPlatformUsage();
                setStats(data);
            } catch (err) {
                setError(err.message);
            } finally {
                setIsLoading(false);
            }
        };
        loadStats();
    }, []);

    if (isLoading) {
        return (
            <div className="admin-loading">
                <div className="spinner"></div>
                <p>Loading dashboard...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="admin-error">
                <h2>Error loading dashboard</h2>
                <p>{formatErrorMessage(error)}</p>
                <button onClick={() => window.location.reload()}>Retry</button>
            </div>
        );
    }

    return (
        <div className="admin-dashboard">
            <div className="admin-header">
                <h1>Admin Dashboard</h1>
                <p>Platform overview and statistics</p>
            </div>

            {/* Stats Cards */}
            <div className="stats-grid">
                <div className="stat-card">
                    <div className="stat-icon users"><UsersIcon /></div>
                    <div className="stat-content">
                        <span className="stat-value">{stats?.total_users || 0}</span>
                        <span className="stat-label">Total Users</span>
                    </div>
                    <div className="stat-trend positive">
                        +{stats?.recent_signups || 0} this month
                    </div>
                </div>

                <div className="stat-card">
                    <div className="stat-icon active"><ActiveIcon /></div>
                    <div className="stat-content">
                        <span className="stat-value">{stats?.active_users || 0}</span>
                        <span className="stat-label">Active Users</span>
                    </div>
                    <div className="stat-trend">Last 30 days</div>
                </div>

                <div className="stat-card">
                    <div className="stat-icon projects"><FolderIcon /></div>
                    <div className="stat-content">
                        <span className="stat-value">{stats?.total_projects || 0}</span>
                        <span className="stat-label">Total Projects</span>
                    </div>
                </div>

                <div className="stat-card">
                    <div className="stat-icon deployments"><RocketIcon /></div>
                    <div className="stat-content">
                        <span className="stat-value">{stats?.total_deployments || 0}</span>
                        <span className="stat-label">Deployments</span>
                    </div>
                    <div className="stat-trend positive">
                        +{stats?.recent_deployments || 0} this month
                    </div>
                </div>

                <div className="stat-card wide">
                    <div className="stat-icon tokens"><CoinsIcon /></div>
                    <div className="stat-content">
                        <span className="stat-value">
                            {((stats?.total_tokens_used || 0) / 1000000).toFixed(2)}M
                        </span>
                        <span className="stat-label">Total Tokens Used</span>
                    </div>
                </div>
            </div>

            {/* Tier Distribution */}
            <div className="admin-section">
                <h2>Users by Tier</h2>
                <div className="tier-grid">
                    {['FREE', 'STARTER', 'PRO', 'BUSINESS', 'ENTERPRISE'].map(tier => (
                        <div key={tier} className={`tier-card ${tier}`}>
                            <span className="tier-name">{tier.charAt(0).toUpperCase() + tier.slice(1)}</span>
                            <span className="tier-count">
                                {stats?.users_by_tier?.[tier] || 0}
                            </span>
                        </div>
                    ))}
                </div>
            </div>

            {/* Quick Actions */}
            <div className="admin-section">
                <h2>Quick Actions</h2>
                <div className="quick-actions">
                    <Link to="/admin/users" className="action-card">
                        <span className="action-icon"><UsersIcon /></span>
                        <span className="action-label">Manage Users</span>
                    </Link>
                    <Link to="/admin/projects" className="action-card">
                        <span className="action-icon"><FolderIcon /></span>
                        <span className="action-label">All Projects</span>
                    </Link>
                    {isSuperAdmin && (
                    <Link to="/admin/tier-limits" className="action-card">
                        <span className="action-icon"><CoinsIcon /></span>
                        <span className="action-label">Tier Limits</span>
                    </Link>
                    )}
                </div>
            </div>
        </div>
    );
}

export default AdminDashboard;
